import { useCallback, useEffect, useRef, useState } from 'react'
import type { HeroMedia } from '../api/types'

/**
 * De carousel bovenaan de landing: op mobiel 288px hoog over de volle breedte,
 * op desktop de linkerhelft over de volle hoogte.
 *
 * Scroll-snap in plaats van een timer of een slider-library. Dat is precies wat
 * de bundle laat zien, het veegt op een telefoon zoals het hoort, en het werkt
 * zonder JavaScript als het beeld nog aan het laden is.
 */
export function HeroCarousel({ items }: { items: HeroMedia[] }) {
  const track = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(0)

  // Geen scroll-listener met een debounce: de waarnemer vuurt alleen als er
  // echt een ander beeld in beeld staat.
  useEffect(() => {
    const element = track.current

    if (!element || items.length < 2) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActive(Number((entry.target as HTMLElement).dataset.index))
          }
        }
      },
      { root: element, threshold: 0.6 },
    )

    for (const slide of element.children) {
      observer.observe(slide)
    }

    return () => observer.disconnect()
  }, [items.length])

  const scrollTo = useCallback((index: number) => {
    const element = track.current

    if (element) {
      element.scrollTo({ left: index * element.clientWidth, behavior: 'smooth' })
    }
  }, [])

  // Een feest zonder foto's heeft nog geen hero. De arcering uit de bundle
  // blijft dan staan, zodat de landing niet als een leeg vlak begint.
  const slides = items.length > 0 ? items : [null]

  return (
    <div className="relative h-72 w-full lg:h-full">
      <div
        ref={track}
        className="scroll-x-clean flex h-full w-full snap-x snap-mandatory"
        aria-label="Beelden van het feest"
      >
        {slides.map((item, index) => (
          <div
            key={item?.id ?? 'leeg'}
            data-index={index}
            className="placeholder-warm h-full w-full shrink-0 snap-start"
          >
            {item && (
              <img
                src={item.webUrl}
                alt=""
                loading={index === 0 ? 'eager' : 'lazy'}
                className="h-full w-full object-cover"
              />
            )}
          </div>
        ))}
      </div>

      {slides.length > 1 && (
        <div className="absolute inset-x-0 bottom-11 flex justify-center gap-1.5 lg:bottom-8 lg:gap-[7px]">
          {slides.map((item, index) => (
            <button
              key={item?.id ?? index}
              type="button"
              aria-label={`Beeld ${index + 1}`}
              aria-current={index === active}
              onClick={() => scrollTo(index)}
              className={
                'size-1.5 rounded-full transition-colors lg:size-[7px] ' +
                (index === active ? 'bg-accent' : 'bg-ink/20')
              }
            />
          ))}
        </div>
      )}
    </div>
  )
}
